import { Component, OnDestroy, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import { aggregateBy, CompositeFilterDescriptor, filterBy, process, SortDescriptor, State } from '@progress/kendo-data-query';
import { ColumnComponent, DataStateChangeEvent, GridComponent, PageChangeEvent } from '@progress/kendo-angular-grid';

import { CommonFunctions } from '../static/common-functions';
import { BaseBus } from '../models/BaseBus';
import { GlobalsService } from '../services/globals.service';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { Subscription } from 'rxjs';
import { CommonValues } from '../static/CommonValues';

@Component({
    selector: '',
    template: '',
    standalone: false
})
export abstract class BaseGridComponent<T extends BaseBus, D>  implements OnDestroy {
  constructor(public globals: GlobalsService, protected featureFlagService: FeatureFlagService) {
    this.pageSize = this.globals.grid_PageSize;
  }

  @ViewChild('grid') protected grid: GridComponent;
  @ViewChild('detail') protected detail: D;
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  subs_data: Subscription;
  dataLoading: boolean = false;
  pageSize: number;
  skip: number;
  pageSizeNoPaging: number = 0;
  noPaging: boolean = false;
  tt_export: string;
  data: T[];
  gridData: T[];
  originalGridData: T[];
  selectedData: T;
  virtualizationSettings = CommonValues.virtualizationSettings;
  get hasGridData(): boolean {
    return CommonFunctions.hasArrayElementValue(this.gridData);
  }
  get gridDataCount(): number {
    return this.hasGridData ? this.gridData.length : 0;
  }
  public sort: SortDescriptor[] = [];
  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
  }
  public pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
  }
  lastIdLoaded: string = null;
  lastSkip = null;
  public selection: string[] = [];
  setReloadIds(id: string) {
    this.lastIdLoaded = id;
    this.selection = [];
    this.selection.push(this.lastIdLoaded);
    this.lastSkip = this.state.skip;
  }
  reloadIds() {
    if (this.lastIdLoaded) {
      this.selection = [];
      this.selection.push(this.lastIdLoaded);
      this.state.skip = this.lastSkip;
      this.lastIdLoaded = null;
    }

  }
  public iconClass: string = 'fa fa-2x fa-download gridExportBtnClass';
  public exportData: Array<any> = [
    {
      text: 'Excel',
      icon: 'excel'
    }, {
      text: 'Pdf',
      icon: 'pdf'
    },];

  public onSplitButtonClick(e: string): void {
    if (e === "Pdf") {
      this.exportGridToPDF();
    }
    else {
      this.exportGridToExcel();
    }
  }

  public exportGridToPDF(): void {
    this.grid.saveAsPDF();
  }
  public exportGridToExcel(): void {
    this.grid.saveAsExcel();
  }

  public gridNavigable: boolean = false;
  private subs_internalFeatureFlagSet: Subscription;

  ngOnDestroy() {
    CommonFunctions.unsubscribe(this.subs_internalFeatureFlagSet);
  }

  get gridData_Length(): number {
    return CommonFunctions.hasArrayElementValue(this.gridData) ? this.gridData.length : 0;
  }
  get gridLength(): number {
    //TODO BW: set up height for responsive screens.
    return this.gridData_Length > this.globals.grid_LineCount ? this.globals.grid_Height : null;
  }
  cloneData() {
    this.originalGridData = _.cloneDeep(this.gridData);
  }
  hasSearchText: boolean = false;
  searchText: string = "";
  onKeyup(e: any) {
    this.search(e.srcElement.value);
  }
  search(search: any) {
    this.searchText = search;
    this.hasSearchText = this.searchText.length > 0;
    const gridFilters = [];
    this.grid.columnList.forEach((x: ColumnComponent) => {
      if (x.field != undefined && (!x.hidden || x.filter.length > 0)) {
        gridFilters.push({
          field: x.field,
          operator: x.filter === 'text' ? "contains" : 'eq',
          value: search,
          ignoreCase: true
        });
      }
    }
    );
    const result = process(this.data, {
      filter: {
        logic: "or",
        filters: gridFilters
      }
    });
    this.gridData = result.data;
  }
  public state: State = {
    skip: 0,
    take: 1000,
    group: []
  };

  clearSearch() {
    this.searchText = "";
    this.search("");
  }

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains'
  };
  public filter: CompositeFilterDescriptor;
  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.gridData = filterBy(this.originalGridData, filter);
    this.processGridData();

  }
  public aggregates: any[] = [];
  public aggregateResults;

  public dataStateChange(state: DataStateChangeEvent): void {
    if (state && state.group) {
      state.group.map(group => group.aggregates = this.aggregates);
    }
    this.state = state;
  }
  processGridData() {
    if (CommonFunctions.hasArrayElementValue(this.gridData)) {
      this.aggregateResults = aggregateBy(this.gridData, this.aggregates);
    }
  }
  public showTooltip(e: MouseEvent): void {
    const element = e.target as HTMLElement;
    if ((element.nodeName === 'TD' || element.nodeName === 'TH')
      && element.offsetWidth < element.scrollWidth) {
      this.tooltipDir.toggle(element);
    } else {
      this.tooltipDir.hide();
    }
  }
  public allData(indata?: ExcelExportData): ExcelExportData {
    const result: ExcelExportData = {
      data: process(this.gridData as any, {}).data,
    };
    return result;
  }
}

